/** @jsxImportSource @emotion/react */
import React, { EventHandler, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import useSessionStore from 'stores/useSessionStore';
import { hasRole } from 'utils/SessionUtil';
import CustomDialog from './CustomDialog';
import { tb } from '../../layouts/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import Divider from '@mui/material/Divider';
import { saveSendMailInfo } from '../../../apis/ut/UtMatrixList';
import { UtMatrixMaster } from '../../../models/ut/UtMatrix';
import CustomTextarea from '../../inputs/CustomTextarea';
import { Employee, EmployeeCondition } from '../../../models/admin/Employee';
import { InputBox, SearchBox, SearchBoxRow, SearchCols, SearchRows } from '../../layouts/SearchBox';
import CustomInputWithSearch from '../../inputs/CustomInputWithSearch';
import SearchBoxButtons from '../../buttons/SearchBoxButtons';
import { getEmployeeByCondition } from 'apis/admin/Employee';
import {
  ControlBtnGroup,
  HalfContetntLayout,
  SubTitleGroup,
  SubTitleLayout,
} from '../../layouts/ContentLayout';
import CustomGrid from '../../grids/CustomGrid';
import { useLoading } from '../../process/Loading';
import _ from 'lodash';
import { CustomInputWrap } from '../../inputs/CustomInput';
import { saveApproveSendMailInfo } from 'apis/approves/approves';

type Props = {
  open: boolean;
  close: () => void;
  condition?: any;
};

/**
 * @desc 결재 공유메일 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.01.20   jihwan89      CSR E20260108-002100 결재문서 임시저장(TMP)/상신취소(CCL)가 아닌 상태의 결재 공유메일 추가
 */

const SendEmailModal = ({ open, close, condition }: Props) => {
  const { t } = useTranslation();
  const [sendMailMsg, setSendMailMsg] = useState<string>('');

  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const handleSendButton = (e) => {
    if (rowDataSeleted.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.수신자를 선택해 주세요.', '수신자를 선택해 주세요.'),
      });
      return;
    }
    sendMailInfo();
  };
  const sendMailInfo = () => {
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.발송하시겠습니까?', '발송하시겠습니까?'),
      yesCallback: () => {
        const sendMaildata = rowDataSeleted.map((row) => ({
          ...row,
          aprReqId: condition?.aprReqId || '',
          emlTpId: row?.userId || '',
          sendMailMsg: sendMailMsg,
        }));
        console.log('sendMaildata', sendMaildata);
        saveApproveSendMailInfo(sendMaildata).then((result) => {
          if (!result) {
            openMessageBar({
              type: 'error',
              content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            return false;
          } else {
            openMessageBar({
              type: 'confirm',
              content: t('com.label.발송되었습니다.', '발송되었습니다.'),
            });
            close();
          }
        });
      },
      noCallback: () => {
        return false;
      },
    });
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleSendButton}
      disableRipple
    >
      {t('com.button.전송', '전송')}
    </Button>,
  ];

  // 사용자 조회 start ..
  const searchRef = useRef<any>();
  const gridRefL = useRef<any>();
  const gridRefR = useRef<any>();
  const [rowDataUser, setRowDataUser] = useState<any[]>([]);
  const [rowDataSeleted, setRowDataSelected] = useState<Employee[]>([]);
  const [checkedItems, setCheckedItems] = useState<Employee[]>([]);
  const [checkedItemsR, setCheckedItemsR] = useState<Employee[]>([]);

  const { openLoading } = useLoading();
  const [conditionUser, setConditionUser] = useState<EmployeeCondition>({
    userId: '',
    empNm: '',
    deptNm: '',
    useYn: 'Y',
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch({ ...conditionUser, useYn: 'Y' });
    }
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setConditionUser({ ...conditionUser, [e.target.name]: e.target.value });
  };

  const onHandleSearch = async (conditionUser: EmployeeCondition) => {
    handleSearch({ ...conditionUser, useYn: 'Y' });
  };

  const handleAddRow = () => {
    addRowData(checkedItems);
  };

  const addRowData = (items: Employee[]) => {
    setRowDataSelected((prev) => {
      const array = [...prev, ...(items || [])];
      const ids = array.map((o) => o.userId).filter((o) => !!o);
      const result = array.filter((o, idx) => ids.indexOf(o.userId || '') === idx);
      return _.sortBy(result, ['empNm']);
    });
  };

  const handleRemoveRow = () => {
    setRowDataSelected((prev) => (prev || []).filter((o) => !(checkedItemsR || []).includes(o)));
  };

  const handleSearch = (conditionUser: EmployeeCondition) => {
    openLoading(true);
    getEmployeeByCondition(conditionUser)
      .then((result) => {
        setRowDataUser(result || []);
      })
      .finally(() => openLoading(false));
  };

  /**
   * search user grid init
   * @param grid
   */
  const onInitializedL = (grid) => {
    gridRefL.current = grid;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const binding = grid.columns[ht.col].binding;
      const item = grid.rows[ht.row].dataItem;
      if (ht.panel === grid.cells) {
        // Quick Select
        if ('btnSelect' === binding) {
          console.log('btnSelect');
        }
      }
    });
  };

  /**
   * search user layout
   */
  const layoutDefinitionL = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      header: String(t('com.label.사용자ID', '사용자ID')),
      binding: 'userId',
      align: 'left',
      width: 150,
    },
    {
      binding: 'empNm',
      header: String(t('com.label.사용자명', '사용자명')),
      width: 120,
    },
    {
      binding: 'deptNm',
      header: String(t('com.label.부서명', '부서명')),
      align: 'left',
      width: '*',
    },
  ];

  /**
   * selected user grid init
   * @param grid
   */
  const onInitializedR = (grid) => {
    gridRefR.current = grid;
  };

  /**
   * selected user grid layout
   */
  const layoutDefinitionR = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      header: String(t('com.label.사용자ID', '사용자ID')),
      binding: 'userId',
      align: 'left',
      width: 150,
    },
    {
      binding: 'empNm',
      header: String(t('com.label.사용자명', '사용자명')),
      width: 120,
    },
    {
      binding: 'deptNm',
      header: String(t('com.label.부서명', '부서명')),
      align: 'left',
      width: '*',
    },
  ];

  return (
    <CustomDialog
      title={t('com.label.결재 메일 공유', '결재 메일 공유')}
      size="lg"
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      {/* 사용자 선택 */}
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('com.label.사용자ID', '사용자ID')}</label>
                <CustomInputWithSearch
                  name="userId"
                  placeholder={String(
                    t('com.label.사용자 ID를 입력해 주세요.', '사용자 ID를 입력해 주세요.')
                  )}
                  value={conditionUser?.userId}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.사용자명', '사용자명')}</label>
                <CustomInputWithSearch
                  name="empNm"
                  placeholder={String(
                    t('com.label.사용자명을 입력해 주세요.', '사용자명을 입력해 주세요.')
                  )}
                  value={conditionUser?.empNm}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.부서명', '부서명')}</label>
                <CustomInputWithSearch
                  name="deptNm"
                  placeholder={String(
                    t('com.label.부서명을 입력해 주세요.', '부서명을 입력해 주세요.')
                  )}
                  value={conditionUser?.deptNm}
                  onChange={handleConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={{ useYn: 'Y' }}
            setReset={setConditionUser}
            onSearch={() => onHandleSearch(conditionUser)}
          />
        </SearchBoxRow>
      </SearchBox>

      <HalfContetntLayout style={{ marginTop: '12px' }}>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.사용자 목록', '사용자 목록')}</h3>
              <span className="total">
                {t('com.label.총', '총')} <span>{(rowDataUser || []).length.toLocaleString()}</span>
                {t('com.label.명', '명')}
              </span>
            </SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="addRow"
                onClick={handleAddRow}
                disableRipple
              >
                {t('com.label.추가', '추가')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            layoutDefinition={layoutDefinitionL}
            rowData={rowDataUser}
            height={200}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            rowSelection={'multiple'}
            allowPinning={false}
            align="center"
            initialized={onInitializedL}
            onChangeCheckedItem={(items) => setCheckedItems(items)}
          />
        </div>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>수신자 목록</h3>
              <span className="total">
                {t('com.label.총', '총')}{' '}
                <span>{(rowDataSeleted || []).length.toLocaleString()}</span>
                {t('com.label.명', '명')}
              </span>
            </SubTitleGroup>
            <ControlBtnGroup>
              <Button
                css={IconButton.button}
                className="delRow"
                onClick={handleRemoveRow}
                disableRipple
              >
                {t('com.label.삭제', '삭제')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            layoutDefinition={layoutDefinitionR}
            rowData={rowDataSeleted}
            height={200}
            isReadOnly={true}
            isFilter={false}
            autoCheck={true}
            rowSelection={'multiple'}
            allowPinning={false}
            allowSorting={false}
            align="center"
            initialized={onInitializedR}
            onChangeCheckedItem={(items) => setCheckedItemsR(items)}
          />
        </div>
      </HalfContetntLayout>

      {/* 사용자 선택 END */}
      <Divider style={{ marginTop: '12px', marginBottom: '12px' }} />
      <TableContainer css={tb.table} className="colFix9">
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <span>{t('com.label.결재', '결재')}</span>
              </TableCell>
              <TableCell>
                <span>[{condition?.aprReqId}] &nbsp;</span>
                <span>{condition?.aprReqTitNm}</span>
                {/*<span>&nbsp;&nbsp;&nbsp;&nbsp; - {condition?.utmId}</span>*/}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {/*className="dot" 필수 필수  */}
                {/*style={{ height: '200px' }}*/}
                <span>{t('com.label.발송내용', '발송내용')}</span>
              </TableCell>
              <TableCell>
                <CustomTextarea
                  name="sendMailMsg"
                  placeholder={String(
                    t('com.label.메일발송내용을 입력해주세요.', '메일발송내용을 입력해주세요.')
                  )}
                  value={sendMailMsg}
                  // isError={errors()?.oldRevCtn}
                  // msgError={String(
                  //   t('com.label.메일발송내용을 입력해주세요.', '메일발송내용을 입력해주세요.') 3
                  // )}
                  onChange={(event: any) => {
                    const value = event?.target.value;
                    if (value.length <= 1000) {
                      setSendMailMsg(value);
                    } else {
                      openMessageBar({
                        type: 'error',
                        content: t(
                          'com.label.메일 내용은 1000자를 넘을 수 없습니다.',
                          '메일 내용은 1000자를 넘을 수 없습니다.'
                        ),
                      });
                    }
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </CustomDialog>
  );
};

export default SendEmailModal;
